import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "../lib/queryClient";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
// import { Separator } from "../components/ui/separator";
import { format } from "date-fns";
import { Bell, CheckCircle, AlertTriangle, AlertCircle, Info } from "lucide-react";
import { getNotifications, markNotificationRead } from "../api/axios";

export default function Notifications() {
  const [activeTab, setActiveTab] = useState<string>("unread");

  // Get notifications from backend (auth from cookie/header)
  const { data: notifications = [], isLoading: isLoadingNotifications } = useQuery({
    queryKey: ["/api/notifications"],
    queryFn: getNotifications,
    staleTime: 1 * 60 * 1000,
  });

  // Mark a notification as read (backend PATCH /notifications/:id/read)
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string | number) => markNotificationRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  // Mark all notifications as read (backend: call mark read for each unread)
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const unread = notifications.filter((n: any) => !n.read);
      await Promise.all(unread.map((n: any) => markNotificationRead(n._id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  // Handle marking a notification as read
  const handleMarkAsRead = (notificationId: string | number) => {
    markAsReadMutation.mutate(notificationId);
  };

  // Handle marking all notifications as read
  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  // Filter notifications based on active tab (backend uses resourceType; support category for compatibility)
  const filteredNotifications = notifications.filter((notification: any) => {
    const category = notification.category ?? notification.resourceType;
    if (activeTab === "unread") return !notification.read;
    if (activeTab === "alerts") return category === "alert";
    if (activeTab === "system") return category === "system";
    return true; // "all" tab
  });

  // Get unread count for badge
  const unreadCount = notifications.filter((n: any) => !n.read).length;

  // Format date relative to now (e.g., "2 hours ago")
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return format(date, "PPp");
  };

  // Get icon based on notification severity
  const getIcon = (severity: string) => {
    switch (severity) {
      case "critical":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case "high":
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case "warning":
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case "info":
        return <Info className="h-5 w-5 text-blue-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  // Get severity badge
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case "critical":
        return (
          <Badge variant="destructive">Critical</Badge>
        );
      case "high":
        return (
          <Badge className="bg-orange-500">High</Badge>
        );
      case "warning":
        return (
          <Badge variant="outline" className="text-yellow-500 border-yellow-500">
            Warning
          </Badge>
        );
      case "info":
        return (
          <Badge variant="outline" className="text-blue-500 border-blue-500">
            Info
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Notifications</h1>
        
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" onClick={handleMarkAllAsRead}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Mark all as read
            </Button>
          )}
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="unread" className="relative">
              Unread
              {unreadCount > 0 && (
                <Badge className="ml-2 bg-primary px-1.5 absolute -top-1 -right-1" variant="default">
                  {unreadCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="alerts">Alerts</TabsTrigger>
            <TabsTrigger value="system">System</TabsTrigger>
          </TabsList>
        </div>
        
        <TabsContent value="unread" className="space-y-4">
          {renderNotificationsList(filteredNotifications, "unread")}
        </TabsContent>
        
        <TabsContent value="all" className="space-y-4">
          {renderNotificationsList(filteredNotifications, "all")}
        </TabsContent>
        
        <TabsContent value="alerts" className="space-y-4">
          {renderNotificationsList(filteredNotifications, "alerts")}
        </TabsContent>
        
        <TabsContent value="system" className="space-y-4">
          {renderNotificationsList(filteredNotifications, "system")}
        </TabsContent>
      </Tabs>
    </div>
  );
  
  // Helper function to render notifications list
  function renderNotificationsList(notifications: any[], currentTab: string) {
    if (isLoadingNotifications) {
      return (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">Loading notifications...</p>
          </CardContent>
        </Card>
      );
    }
    
    if (notifications.length === 0) {
      return (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground">
              {currentTab === "unread" 
                ? "No unread notifications" 
                : currentTab === "alerts" 
                ? "No alert notifications"
                : currentTab === "system"
                ? "No system notifications"
                : "No notifications"}
            </p>
          </CardContent>
        </Card>
      );
    }
    
    return (
      <Card>
        <CardContent className="p-0">
          <ul className="divide-y divide-border">
            {notifications.map((notification: any) => (
              <li key={notification._id || notification.id} className={`p-4 ${!notification.read ? "bg-muted/30" : ""}`}>
                <div className="flex items-start gap-4">
                  <div className="pt-1">
                    {getIcon(notification.severity)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-base font-medium">{notification.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {notification.message}
                        </p>
                        
                        {notification.resourceId && (
                          <div className="mt-2">
                            <Badge variant="outline" className="text-xs">
                              {notification.resourceType} ID: {notification.resourceId}
                            </Badge>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col items-end">
                        <div className="text-xs text-muted-foreground">
                          {formatDate(notification.timestamp)}
                        </div>
                        {getSeverityBadge(notification.severity)}
                      </div>
                    </div>
                    
                    {!notification.read && (
                      <div className="mt-3 flex justify-end">
                        <Button 
                          variant="ghost"
                          size="sm"
                          className="h-8"
                          onClick={() => handleMarkAsRead(notification._id || notification.id)}
                        >
                          <CheckCircle className="mr-2 h-3.5 w-3.5" />
                          Mark as read
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    );
  }
}